import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { Task, Team, User, RecurrenceType } from '../../types';

interface TaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (taskData: any) => void;
  task?: Task | null;
  teams: Team[];
  users: User[];
  projects?: any[];
  isLoading: boolean;
  creatorId?: string;
  hideProjectSelection?: boolean; // New prop to hide project selection
  initialData?: {
    dueDate?: string;
    startDate?: string;
    teamId?: string;
    projectId?: string;
    assigneeIds?: string[];
  };
}

const TaskModal: React.FC<TaskModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  task,
  teams,
  users,
  projects = [],
  isLoading,
  creatorId,
  hideProjectSelection = false,
  initialData
}) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    status: 'TODO',
    priority: 'NORMAL',
    teamId: '',
    projectId: '',
    dueDate: '',
    startDate: '',
    endDate: '',
    estimatedHours: 0,
    assigneeIds: [] as string[],
    // Recurring task fields
    isRecurring: false,
    recurrenceType: 'WEEKLY' as RecurrenceType,
    recurrenceValue: 1,
    daysOfWeek: [] as number[],
    dayOfMonth: 1,
    maxOccurrences: undefined as number | undefined,
  });

  useEffect(() => {
    if (task) {
      setFormData({
        title: task.title,
        description: task.description || '',
        status: task.status,
        priority: task.priority,
        teamId: task.team?.id || '',
        projectId: task.project?.id || '',
        dueDate: task.dueDate ? new Date(task.dueDate).toISOString().split('T')[0] : '',
        startDate: task.startDate ? new Date(task.startDate).toISOString().split('T')[0] : '',
        endDate: task.endDate ? new Date(task.endDate).toISOString().split('T')[0] : '',
        estimatedHours: task.estimatedHours || 0,
        assigneeIds: task.assignees?.map(a => a.user.id) || [],
        // Recurring task fields - reset for editing existing tasks
        isRecurring: false,
        recurrenceType: 'WEEKLY' as RecurrenceType,
        recurrenceValue: 1,
        daysOfWeek: [] as number[],
        dayOfMonth: 1,
        maxOccurrences: undefined as number | undefined,
      });
    } else {
      // Auto-select project only if hideProjectSelection is true (project context)
      const autoProjectId = initialData?.projectId || (hideProjectSelection && projects.length === 1 ? projects[0].id : '');

      setFormData({
        title: '',
        description: '',
        status: 'TODO',
        priority: 'NORMAL',
        teamId: initialData?.teamId || '',
        projectId: autoProjectId,
        dueDate: initialData?.dueDate ? new Date(initialData.dueDate).toISOString().split('T')[0] : '',
        startDate: initialData?.startDate ? new Date(initialData.startDate).toISOString().split('T')[0] : '',
        endDate: '',
        estimatedHours: 0,
        assigneeIds: initialData?.assigneeIds || [],
        // Recurring task fields
        isRecurring: false,
        recurrenceType: 'WEEKLY' as RecurrenceType,
        recurrenceValue: 1,
        daysOfWeek: [] as number[],
        dayOfMonth: 1,
        maxOccurrences: undefined as number | undefined,
      });
    }
  }, [task, initialData, projects, hideProjectSelection]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.isRecurring) {
      // Prepare recurring task data
      const recurringTaskData = {
        title: formData.title,
        description: formData.description,
        priority: formData.priority,
        status: formData.status,
        teamId: formData.teamId || undefined,
        projectId: formData.projectId || undefined,
        creatorId: creatorId || 'cmdd8jvdu0001v1hsfzjmmox6', // Fallback to default user
        assigneeIds: formData.assigneeIds,
        recurrenceType: formData.recurrenceType,
        recurrenceValue: formData.recurrenceValue,
        daysOfWeek: formData.recurrenceType === 'WEEKLY' ? formData.daysOfWeek : undefined,
        dayOfMonth: formData.recurrenceType === 'MONTHLY' ? formData.dayOfMonth : undefined,
        startDate: formData.dueDate && formData.dueDate.trim() !== '' ? new Date(formData.dueDate).toISOString() : undefined,
        endDate: formData.endDate && formData.endDate.trim() !== '' ? new Date(formData.endDate).toISOString() : undefined,
        maxOccurrences: formData.maxOccurrences,
        isActive: true,
      };

      // Include required fields at top level for validation, plus the nested recurring data
      const submitData = {
        title: formData.title, // Required for validation
        creatorId: creatorId || 'cmdd8jvdu0001v1hsfzjmmox6', // Required for validation
        isRecurring: true,
        recurringTaskData,
      };
      onSubmit(submitData);
    } else {
      // Regular task submission - exclude recurring task fields
      const { recurrenceType, recurrenceValue, daysOfWeek, dayOfMonth, maxOccurrences, isRecurring, ...regularTaskData } = formData;

      const submitData = {
        ...regularTaskData,
        dueDate: formData.dueDate && formData.dueDate.trim() !== '' ? new Date(formData.dueDate).toISOString() : null,
        startDate: formData.startDate && formData.startDate.trim() !== '' ? new Date(formData.startDate).toISOString() : undefined,
        endDate: formData.endDate && formData.endDate.trim() !== '' ? new Date(formData.endDate).toISOString() : undefined,
      };
      onSubmit(submitData);
    }
  };

  const handleAssigneeChange = (userId: string, checked: boolean) => {
    if (checked) {
      setFormData({
        ...formData,
        assigneeIds: [...formData.assigneeIds, userId]
      });
    } else {
      setFormData({
        ...formData,
        assigneeIds: formData.assigneeIds.filter(id => id !== userId)
      });
    }
  };

  const handleDayOfWeekChange = (day: number, checked: boolean) => {
    if (checked) {
      setFormData({
        ...formData,
        daysOfWeek: [...formData.daysOfWeek, day].sort()
      });
    } else {
      setFormData({
        ...formData,
        daysOfWeek: formData.daysOfWeek.filter(d => d !== day)
      });
    }
  };

  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          {/* Background overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-gray-500 bg-opacity-75"
            onClick={onClose}
            aria-hidden="true"
          />

          {/* Modal Container */}
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            {/* This element is to trick the browser into centering the modal contents. */}
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
              &#8203;
            </span>

            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative inline-block w-full max-w-2xl p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl z-10"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900">
                  {task ? 'Edit Task' : 'Create New Task'}
                </h2>
                <button
                  onClick={onClose}
                  className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                    Task Title *
                  </label>
                  <input
                    type="text"
                    id="title"
                    required
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Enter task title"
                  />
                </div>

                <div>
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    id="description"
                    rows={3}
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Enter task description"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
                      Status
                    </label>
                    <select
                      id="status"
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="TODO">To Do</option>
                      <option value="IN_PROGRESS">In Progress</option>
                      <option value="DONE">Done</option>
                      <option value="CANCELLED">Cancelled</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="priority" className="block text-sm font-medium text-gray-700 mb-1">
                      Priority
                    </label>
                    <select
                      id="priority"
                      value={formData.priority}
                      onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="LOW">Low</option>
                      <option value="NORMAL">Normal</option>
                      <option value="HIGH">High</option>
                      <option value="URGENT">Urgent</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label htmlFor="teamId" className="block text-sm font-medium text-gray-700 mb-1">
                    Team
                  </label>
                  <select
                    id="teamId"
                    value={formData.teamId}
                    onChange={(e) => setFormData({ ...formData, teamId: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="">No team</option>
                    {teams.map((team) => (
                      <option key={team.id} value={team.id}>
                        {team.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-1">
                      Start Date
                    </label>
                    <input
                      type="date"
                      id="startDate"
                      value={formData.startDate}
                      onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>

                  <div>
                    <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 mb-1">
                      End Date
                    </label>
                    <input
                      type="date"
                      id="endDate"
                      value={formData.endDate}
                      onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>

                  <div>
                    <label htmlFor="dueDate" className="block text-sm font-medium text-gray-700 mb-1">
                      Due Date
                    </label>
                    <input
                      type="date"
                      id="dueDate"
                      value={formData.dueDate}
                      onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                </div>

                {/* Estimated Hours */}
                <div>
                  <label htmlFor="estimatedHours" className="block text-sm font-medium text-gray-700 mb-1">
                    Estimated Hours
                  </label>
                  <input
                    type="number"
                    id="estimatedHours"
                    min="0"
                    step="0.5"
                    value={formData.estimatedHours}
                    onChange={(e) => setFormData({ ...formData, estimatedHours: Number(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Enter estimated hours for this task"
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    Set the estimated time needed to complete this task. This helps with planning and tracking progress.
                  </p>
                </div>

                {/* Only show project selection if not explicitly hidden */}
                {!hideProjectSelection && (
                  <div>
                    <label htmlFor="projectId" className="block text-sm font-medium text-gray-700 mb-1">
                      Project Assignment
                    </label>
                    <select
                      id="projectId"
                      value={formData.projectId}
                      onChange={(e) => setFormData({ ...formData, projectId: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="">Standalone Task (No Project)</option>
                      {projects.map((project) => (
                        <option key={project.id} value={project.id}>
                          {project.name}
                        </option>
                      ))}
                    </select>
                    <p className="mt-1 text-sm text-gray-500">
                      Leave empty to create a standalone task, or select a project to assign this task to it.
                    </p>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Assignees
                  </label>
                  <div className="max-h-32 overflow-y-auto border border-gray-300 rounded-md p-2">
                    {users.length === 0 ? (
                      <p className="text-sm text-gray-500">No users available</p>
                    ) : (
                      <div className="space-y-2">
                        {users.map((user) => (
                          <label key={user.id} className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              checked={formData.assigneeIds.includes(user.id)}
                              onChange={(e) => handleAssigneeChange(user.id, e.target.checked)}
                              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                            />
                            <span className="text-sm text-gray-700">
                              {user.firstName} {user.lastName} ({user.email})
                            </span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Recurring Task Options */}
                <div className="border-t pt-4">
                  <div className="flex items-center space-x-2 mb-4">
                    <input
                      type="checkbox"
                      id="isRecurring"
                      checked={formData.isRecurring}
                      onChange={(e) => setFormData({ ...formData, isRecurring: e.target.checked })}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <label htmlFor="isRecurring" className="text-sm font-medium text-gray-700">
                      Make this a recurring task
                    </label>
                  </div>

                  {formData.isRecurring && (
                    <div className="space-y-4 bg-gray-50 p-4 rounded-md">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label htmlFor="recurrenceType" className="block text-sm font-medium text-gray-700 mb-1">
                            Repeat Pattern
                          </label>
                          <select
                            id="recurrenceType"
                            value={formData.recurrenceType}
                            onChange={(e) => setFormData({ ...formData, recurrenceType: e.target.value as RecurrenceType })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                          >
                            <option value="DAILY">Daily</option>
                            <option value="WEEKLY">Weekly</option>
                            <option value="MONTHLY">Monthly</option>
                            <option value="YEARLY">Yearly</option>
                          </select>
                        </div>

                        <div>
                          <label htmlFor="recurrenceValue" className="block text-sm font-medium text-gray-700 mb-1">
                            Every
                          </label>
                          <div className="flex items-center space-x-2">
                            <input
                              type="number"
                              id="recurrenceValue"
                              min="1"
                              max="365"
                              value={formData.recurrenceValue}
                              onChange={(e) => setFormData({ ...formData, recurrenceValue: parseInt(e.target.value) || 1 })}
                              className="w-20 px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                            />
                            <span className="text-sm text-gray-600">
                              {formData.recurrenceType.toLowerCase()}(s)
                            </span>
                          </div>
                        </div>
                      </div>

                      {formData.recurrenceType === 'WEEKLY' && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Days of the Week
                          </label>
                          <div className="grid grid-cols-4 gap-2">
                            {dayNames.map((day, index) => (
                              <label key={index} className="flex items-center space-x-2">
                                <input
                                  type="checkbox"
                                  checked={formData.daysOfWeek.includes(index)}
                                  onChange={(e) => handleDayOfWeekChange(index, e.target.checked)}
                                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                />
                                <span className="text-sm text-gray-700">{day.slice(0, 3)}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      )}

                      {formData.recurrenceType === 'MONTHLY' && (
                        <div>
                          <label htmlFor="dayOfMonth" className="block text-sm font-medium text-gray-700 mb-1">
                            Day of Month
                          </label>
                          <input
                            type="number"
                            id="dayOfMonth"
                            min="1"
                            max="31"
                            value={formData.dayOfMonth}
                            onChange={(e) => setFormData({ ...formData, dayOfMonth: parseInt(e.target.value) || 1 })}
                            className="w-20 px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                          />
                        </div>
                      )}

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 mb-1">
                            End Date (Optional)
                          </label>
                          <input
                            type="date"
                            id="endDate"
                            value={formData.endDate}
                            onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                          />
                        </div>

                        <div>
                          <label htmlFor="maxOccurrences" className="block text-sm font-medium text-gray-700 mb-1">
                            Max Occurrences (Optional)
                          </label>
                          <input
                            type="number"
                            id="maxOccurrences"
                            min="1"
                            max="1000"
                            value={formData.maxOccurrences || ''}
                            onChange={(e) => setFormData({ ...formData, maxOccurrences: e.target.value ? parseInt(e.target.value) : undefined })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                            placeholder="No limit"
                          />
                        </div>
                      </div>

                      <div className="text-sm text-gray-600 bg-blue-50 p-3 rounded-md">
                        <p className="font-medium text-blue-800 mb-1">Preview:</p>
                        <p>
                          This task will repeat every {formData.recurrenceValue} {formData.recurrenceType.toLowerCase()}
                          {formData.recurrenceType === 'WEEKLY' && formData.daysOfWeek.length > 0 &&
                            ` on ${formData.daysOfWeek.map(d => dayNames[d].slice(0, 3)).join(', ')}`}
                          {formData.recurrenceType === 'MONTHLY' && ` on day ${formData.dayOfMonth}`}
                          {formData.endDate && ` until ${new Date(formData.endDate).toLocaleDateString()}`}
                          {formData.maxOccurrences && ` for up to ${formData.maxOccurrences} occurrences`}
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoading ? 'Saving...' : task ? 'Update Task' : 'Create Task'}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default TaskModal;
